import { acceptHMRUpdate, defineStore } from 'pinia';
import type { LocationQuery } from 'vue-router';
import type { Attribute } from '@hypercodestudio/basler-components/dist/components/modules/ProductDetailFeatures.vue';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import { createOptions } from './helper/createOptions';
import { findNextValidConfiguration } from './helper/findNextValidConfiguration';
import {
  DecoratedProduct,
  type ProductEnrichmentRules
} from '~/utils/shop/DecoratedProduct';
import { createValidOptions } from './helper/createValidOptions';
import { fillUpMissingOptions } from './helper/fillUpMissingOptions';
import {
  type ConfigurableProduct,
  isConfigurableProduct as isConfigurableProductHelper
} from './helper/isConfigurableProduct';
import type {
  CommonProductDetailFragment,
  CommonProductFragment,
  ConfigurableOptionFragment,
  ConfigurableVariantFragment,
  ParentProductsFragment,
  ParentVariantFragment,
  ProductDetailFragment
} from '~/lib/Shop/generated/schema';
import { isDefined } from '~/utils/guards/isDefined';
import { createHighlightedSpecifications } from '~/utils/shop/createSpecifications';
import { extractError } from '~/utils/extractError';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import type { PdpConfiguration } from '~/composables/usePdpConfiguration';
import type { LOCALE_CODE } from '~/lib/ContentfulService';
import { isBundleProduct as isBundleProductHelper } from '~/stores/productDetailPageStore/helper/isBundleProduct';
import { useProductAddonsStore } from '~/stores/productDetailPageStore/productAddonsStore';
import { StatusCodes } from 'http-status-codes';

export type ProductDetailPageStoreState = {
  urlKey: string;
  /**
   * @deprecated use `$shopStore` from `useNuxtApp` instead.
   */
  store: string;
  /**
   * @deprecated use `$locale` from `useNuxtApp` instead.
   */
  locale: string;
  selectedOptions: Record<string, string>;
  loading: boolean;
  error: string | null;
  abortController: AbortController | null;
  /**
   * A product cache with the sku as key and the product response as value.
   */
  productCache: Record<string, ProductDetailFragment>;
  // XXX: add abstraction
  product: ProductDetailFragment | null;
  variant: CommonProductFragment | CommonProductDetailFragment | null;
  /**
   * @deprecated use {@link usePdpConfiguration}.
   */
  specificationConfiguration: PdpConfiguration | null;
  vscPageLink: HyperlinkInterface | null;
  pdpActionBoxLink: HyperlinkInterface | null;
};

function isSimpleProduct(
  product?: { __typename: string } | null
): product is ParentProductsFragment & { __typename: 'SimpleProduct' } {
  return product?.__typename === 'SimpleProduct';
}

export const useProductDetailPageStore = defineStore(
  'product-detail-page',
  () => {
    const { $shopStore, $locale } = useNuxtApp();
    const nuxtApp = useNuxtApp();
    const urlKey = ref<string>('');
    const selectedOptions = ref<Record<string, string>>({});
    const loading = ref<boolean>(false);
    const error = ref<string | null>(null);
    const abortController = ref<AbortController | null>(null);
    /**
     * A product cache with the sku as key and the product response as value.
     */
    const productCache = ref<Record<string, ProductDetailFragment>>({});
    // XXX: add abstraction
    const product = ref<ProductDetailFragment | null>(null);
    const variant = ref<
      CommonProductFragment | CommonProductDetailFragment | null
    >(null);
    /**
     * @deprecated use {@link usePdpConfiguration}.
     */
    const specificationConfiguration = ref<PdpConfiguration | null>(null);
    const vscPageLink = ref<HyperlinkInterface | null>(null);
    const pdpActionBoxLink = ref<HyperlinkInterface | null>(null);

    const _state = {
      urlKey,
      selectedOptions,
      loading,
      error,
      abortController,
      productCache,
      product,
      variant,
      specificationConfiguration,
      vscPageLink,
      pdpActionBoxLink
    };

    const activeProduct = computed<
      CommonProductFragment | CommonProductDetailFragment | null
    >(() => {
      return variant.value ?? product.value;
    });

    const actualDecoratedProduct = computed<DecoratedProduct | null>(() => {
      if (!activeProduct.value) {
        return null;
      }

      return DecoratedProduct.build(
        activeProduct.value,
        nuxtApp?.$textDictionary.value,
        $locale.value
      );
    });

    const bundleProductDisplayProduct = computed<
      CommonProductFragment | CommonProductDetailFragment | undefined
    >(() => {
      const addonStore = useProductAddonsStore();

      return productCache.value[
        createCacheKey.value(addonStore.selectedRequiredItemUrlKey ?? '')
      ];
    });

    const decoratedProduct = computed<DecoratedProduct | null>(() => {
      if (bundleProductDisplayProduct.value) {
        return DecoratedProduct.build(
          bundleProductDisplayProduct.value,
          nuxtApp?.$textDictionary.value,
          $locale.value
        );
      }

      return actualDecoratedProduct.value;
    });

    const isConfigurableProduct = computed<boolean>(() => {
      return isConfigurableProductHelper(activeProduct.value);
    });

    const isBundledProduct = computed<boolean>(() => {
      return isBundleProductHelper(activeProduct.value);
    });

    const highlightedSpecs = computed<Attribute[]>(() => {
      if (!specificationConfiguration.value) {
        return [];
      }

      return createHighlightedSpecifications(
        specificationConfiguration.value,
        decoratedProduct.value?.customAttributes ?? [],
        decoratedProduct.value?.product?.attribute_set_id?.toString() ?? '',
        $locale.value as LOCALE_CODE
      );
    });

    /**
     * A list of *all* available options.
     * For valid options, see {@link availableOptions}.
     */
    const options = computed<ReadonlyArray<ConfigurableOptionFragment>>(() => {
      // options are always from the base product (a configured variant
      // is no longer configurable
      if (
        !(
          isBundleProductHelper(parentProduct.value) ||
          isConfigurableProductHelper(parentProduct.value)
        )
      ) {
        return [];
      }

      return (
        (
          parentProduct.value as unknown as ConfigurableProduct
        )?.configurable_options
          ?.filter(isDefined)
          .sort((optionA, optionB) => {
            if (
              typeof optionA?.position !== 'number' ||
              typeof optionB?.position !== 'number'
            ) {
              return 0;
            }

            return optionA.position - optionB.position;
          }) ?? []
      );
    });

    const productEnrichmentRules = computed<ProductEnrichmentRules>(() => {
      if (!decoratedProduct.value) {
        return {};
      }

      return decoratedProduct.value.createProductEnrichmentRules();
    });
    /**
     * Returns a redirect url_key for the **base product** if given.
     */
    const redirect = computed<
      { urlKey: string; statusCode: number } | undefined
    >(() => {
      if (decoratedProduct.value?.bundleUrlKey) {
        return {
          urlKey: decoratedProduct.value?.bundleUrlKey,
          statusCode: StatusCodes.MOVED_PERMANENTLY
        };
      }

      if (
        product.value &&
        'redirect_code' in product.value &&
        'relative_url' in product.value &&
        [301, 302].includes(product.value.redirect_code) &&
        product.value.relative_url
      ) {
        return {
          urlKey: product.value.relative_url,
          statusCode: product.value.redirect_code
        };
      }
    });

    /**
     * A list of valid options based on the {@link selectedOptions}.
     */
    const availableOptions = computed<Record<string, string[]>>(() => {
      return createValidOptions(
        availableVariants.value,
        selectedOptions.value,
        options.value.map((option) => option.attribute_code).filter(isDefined)
      );
    });

    /**
     * Checks if all necessary options are select (not if the selection is actually valid).
     */
    const isConfigurationComplete = computed<boolean>(() => {
      return (
        Object.keys(options.value).length ===
        Object.keys(selectedOptions.value).length
      );
    });

    /**
     * Checks if each selected options is a valid option.
     * This does *not* imply a finished configuration!
     * See {@link #isInvalidConfiguration}.
     */
    const hasValidConfigurationOptions = computed<boolean>(() => {
      return Object.entries(selectedOptions.value).every(
        ([code, uid]) => availableOptions.value[code]?.includes(uid) ?? false
      );
    });

    /**
     * Checks, if all necessary options are selected and result in a valid configuration.
     * Can be used to reset invalid configuration options.
     */
    const isValidConfiguration = computed<boolean>(() => {
      return (
        isConfigurationComplete.value && hasValidConfigurationOptions.value
      );
    });

    /**
     * Checks, if the configuration is complete but results in invalid selected options.
     */
    const isInvalidConfiguration = computed<boolean>(() => {
      return (
        isConfigurationComplete.value && !hasValidConfigurationOptions.value
      );
    });

    /**
     * Returns the matching variant url key for the selected optiosn if present.
     */
    const selectedVariantUrlKey = computed<string | undefined>(() => {
      return (
        availableVariants.value?.find((variant) =>
          variant?.attributes?.every(
            (attribute) =>
              selectedOptions.value[attribute?.code ?? ''] === attribute?.uid
          )
        )?.product?.url_key ?? undefined
      );
    });

    /**
     * Returns the next valid configuration. E.g. if an invalid configuration is present,
     * this can be used to redirect to a valid configuration.
     */

    const nextValidConfiguration = computed<Record<string, string>>(() => {
      return findNextValidConfiguration(
        availableVariants.value,
        selectedOptions.value,
        options.value.map((option) => option.attribute_code).filter(isDefined)
      );
    });

    const availableVariants = computed<ConfigurableVariantFragment[] | null>(
      () => {
        return parentProduct.value && 'variants' in parentProduct.value
          ? parentProduct.value.variants?.filter(isDefined) ?? null
          : null;
      }
    );

    const createCacheKey = computed(() => {
      return (urlKey: string): string =>
        `${urlKey}-${$shopStore.value}-${$locale.value}`;
    });

    const parentProduct = computed<ConfigurableProduct | null>(() => {
      const newProduct = product.value;

      if (!isSimpleProduct(newProduct)) {
        // XXX: throw error or hide option?
        return null;
      }

      const parentProduct = newProduct?.parent_products?.[0];
      if (!isConfigurableProductHelper(parentProduct)) {
        // XXX: throw error or hide option?
        return null;
      }

      return parentProduct;
    });

    const currentVariant = computed<ParentVariantFragment | null>(() => {
      return (
        parentProduct.value?.variants?.find(
          (variant) => variant?.product?.url_key === product.value?.url_key
        ) ?? null
      );
    });

    const currentVariantOptionUids = computed<string[]>(() => {
      return (
        currentVariant.value?.attributes
          ?.map((attr) => attr?.uid)
          ?.filter(isDefined) ?? []
      );
    });

    async function init(query: LocationQuery): Promise<void> {
      const product = await loadAndSetProduct(urlKey.value);

      if (!product) {
        throw createError({
          statusCode: StatusCodes.NOT_FOUND,
          fatal: true
        });
      }

      if (isConfigurableProductHelper(product)) {
        const newSelectedOptions = createOptions(query, options.value);
        // the selected options should always contain a valid
        // selection for each option.
        // @see https://gcp.baslerweb.com/jira/browse/WEB2-2421
        selectedOptions.value = fillUpMissingOptions(
          availableVariants.value,
          findNextValidConfiguration(
            availableVariants.value,
            newSelectedOptions,
            options.value
              .map((option) => option.attribute_code)
              .filter(isDefined)
          )
        );
        await fetchVariant();
      } else {
        variant.value = null;
        selectedOptions.value = {};
      }

      useProductAddonsStore().setBundledProduct(
        isBundleProductHelper(product) ? toRaw(product) : null
      );
    }

    async function loadProduct(
      urlKey: string
    ): Promise<ProductDetailFragment | undefined> {
      const cacheKey = createCacheKey.value(urlKey);

      if (!productCache.value[cacheKey]) {
        if (!abortController.value?.signal.aborted) {
          abortController.value?.abort();
        }

        const newAbortController = new AbortController();
        abortController.value = newAbortController;

        try {
          loading.value = true;
          productCache.value[cacheKey] = await $fetch<ProductDetailFragment>(
            '/api/magento/product',
            {
              query: {
                store: $shopStore.value,
                urlKey: urlKey,
                locale: $locale.value
              },
              signal: abortController.value?.signal
            }
          ); // reset controller to allow serialization

          abortController.value = null;
          error.value = null;
        } catch (e) {
          if (!newAbortController.signal.aborted) {
            error.value = extractError(e) ?? 'Unable to load configuration.';
          }
        } finally {
          loading.value = false;
        }
      }

      return productCache.value[cacheKey];
    }

    function setActiveProduct(newProduct: ProductDetailFragment): void {
      product.value = newProduct;
    }

    async function loadAndSetProduct(
      urlKey: string
    ): Promise<ProductDetailFragment | undefined> {
      const product = await loadProduct(urlKey);
      if (product) {
        setActiveProduct(product);
      }

      return product;
    }

    /**
     * Resolves selected options to a variant if a configurations is complete
     * and disables further configuration based on the current configuration.
     */

    async function fetchVariant() {
      if (!selectedVariantUrlKey.value) {
        // XXX: redirect to valid configuration?
        // reset a previous selected variant
        variant.value = null;

        return;
      }

      const variantUrlKey = selectedVariantUrlKey.value;
      const cacheKey = createCacheKey.value(variantUrlKey);

      if (!productCache.value[cacheKey]) {
        if (!abortController.value?.signal.aborted) {
          abortController.value?.abort();
        }

        const newAbortController = new AbortController();
        abortController.value = newAbortController;
        loading.value = true;

        try {
          productCache.value[cacheKey] = await $fetch<ProductDetailFragment>(
            '/api/magento/product',
            {
              ...DEFAULT_FETCH_OPTIONS,
              method: 'GET',
              query: {
                urlKey: variantUrlKey,
                store: $shopStore.value,
                locale: $locale.value
              },
              signal: abortController.value?.signal
            }
          );
          // reset controller to allow serialization
          abortController.value = null;
          error.value = null;
        } catch (e) {
          if (!newAbortController.signal.aborted) {
            error.value = extractError(e) ?? 'Unable to load configuration.';
          }
        } finally {
          loading.value = false;
        }
      }

      variant.value = productCache.value[cacheKey];
    }

    async function enhancedPdpActionBoxLink(): Promise<
      HyperlinkInterface | undefined
    > {
      if (
        !decoratedProduct.value ||
        !decoratedProduct.value.pdpActionBoxLinksData ||
        !decoratedProduct.value.pdpActionBoxLinksData?.ctf_entity_id ||
        !decoratedProduct.value.pdpActionBoxLinksData?.ctf_entity_id?.value ||
        !Array.isArray(
          decoratedProduct.value.pdpActionBoxLinksData?.ctf_entity_id?.value
        ) ||
        !decoratedProduct.value.pdpActionBoxLinksData?.ctf_entity_id?.value?.[0]
      ) {
        return;
      }

      try {
        const response = await $fetch(
          `/api/contentful/slugById/${decoratedProduct.value.pdpActionBoxLinksData?.ctf_entity_id?.value?.[0]}?locale=${$locale.value}`
        );

        try {
          const origin = useRequestURL().origin;
          const pathname = buildUrlString($locale.value, response.slug);
          const url = new URL(pathname, origin);
          url.searchParams.append('Quantity', '1');
          if (decoratedProduct.value.productLine) {
            url.searchParams.append(
              'Productgroupinterest',
              decoratedProduct.value.productLine
            );
          }
          if (decoratedProduct.value.sku) {
            url.searchParams.append(
              'SAPMaterialID',
              decoratedProduct.value.sku
            );
          }
          if (decoratedProduct.value.modelName) {
            url.searchParams.append('Model', decoratedProduct.value.modelName);
          }
          if (decoratedProduct.value.interface) {
            url.searchParams.append(
              'Interfaceinterest',
              decoratedProduct.value.interface
            );
          }

          return {
            ...pdpActionBoxLink.value,
            uri: url.toString(),
            target: '_self'
          };
        } catch (e) {
          console.error('failed to create pdp action box link', e);
        }
      } catch (e) {
        console.error('failed to create slug', e);
      }
    }

    return {
      _state,
      urlKey,
      selectedOptions,
      loading,
      error,
      abortController,
      productCache,
      product,
      variant,
      specificationConfiguration,
      vscPageLink,
      pdpActionBoxLink,
      activeProduct,
      actualDecoratedProduct,
      bundleProductDisplayProduct,
      decoratedProduct,
      isConfigurableProduct,
      isBundledProduct,
      highlightedSpecs,
      options,
      productEnrichmentRules,
      redirect,
      availableOptions,
      isConfigurationComplete,
      hasValidConfigurationOptions,
      isValidConfiguration,
      isInvalidConfiguration,
      selectedVariantUrlKey,
      nextValidConfiguration,
      availableVariants,
      createCacheKey,
      parentProduct,
      currentVariant,
      currentVariantOptionUids,
      init,
      loadProduct,
      setActiveProduct,
      loadAndSetProduct,
      fetchVariant,
      enhancedPdpActionBoxLink
    };
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useProductDetailPageStore, import.meta.hot)
  );
}
